'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  AlignLeft,
  Check,
  Columns2,
  Copy,
  Download,
  Minimize2,
  Braces,
  Code2,
  X,
} from 'lucide-react';
import { EditorShell } from '@/components/datahunter/editor-shell';
import { JsonEditorPane } from '@/components/datahunter/json-editor-pane';
import { diffJson, parseJson, shortValue } from '@/lib/json-diff';

/** Кнопка панели: одинаковый вид у всех действий. */
function ToolButton({
  onClick,
  disabled,
  active,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
        active
          ? 'border-[var(--color-text)] bg-[var(--color-bg-elevated)]'
          : 'border-[var(--color-border)] hover:border-[var(--color-text-muted)]'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Редактор JSON.
 *
 * Под капотом vanilla-jsoneditor: дерево, подсветка, сворачивание и поиск
 * по документу, чего textarea дать не может. Работает целиком в браузере,
 * файл никуда не отправляется.
 *
 * Второе поле открывается по требованию: сравнение двух ответов API это
 * основная причина, по которой такие редакторы вообще открывают, но
 * человеку, который просто чинит один документ, вторая панель мешает.
 */
export function JsonEditor({ title }: { title: string }) {
  const t = useTranslations('dh_editor');
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<'text' | 'tree'>('text');
  const [compare, setCompare] = useState(false);
  // Правый документ живёт только во вкладке: это черновик для сравнения,
  // а не работа, к которой человек возвращается.
  const [other, setOther] = useState('');

  return (
    <EditorShell
      kind="json"
      title={title}
      toolbar={({ value, onChange, readOnly }) => (
        <>
          <ToolButton
            active={mode === 'tree'}
            onClick={() => {
              setMode(mode === 'tree' ? 'text' : 'tree');
            }}
          >
            {mode === 'tree' ? <Code2 className="w-4 h-4" /> : <Braces className="w-4 h-4" />}
            {mode === 'tree' ? t('mode_text') : t('mode_tree')}
          </ToolButton>

          <ToolButton
            active={compare}
            onClick={() => {
              setCompare(!compare);
            }}
          >
            <Columns2 className="w-4 h-4" />
            {t('compare')}
          </ToolButton>

          <ToolButton
            disabled={readOnly || !value.trim()}
            onClick={() => {
              const parsed = parseJson(value);
              // Поломанный JSON форматировать нечем: ошибка и так видна
              // под полем ввода, второе сообщение здесь лишнее.
              if (parsed.ok) onChange(JSON.stringify(parsed.value, null, 2));
            }}
          >
            <AlignLeft className="w-4 h-4" />
            {t('format')}
          </ToolButton>

          <ToolButton
            disabled={readOnly || !value.trim()}
            onClick={() => {
              const parsed = parseJson(value);
              if (parsed.ok) onChange(JSON.stringify(parsed.value));
            }}
          >
            <Minimize2 className="w-4 h-4" />
            {t('minify')}
          </ToolButton>

          <ToolButton
            disabled={!value}
            onClick={() => {
              void navigator.clipboard.writeText(value).then(() => {
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 2000);
              });
            }}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t('copied') : t('copy')}
          </ToolButton>

          <ToolButton
            disabled={!value}
            onClick={() => {
              const url = URL.createObjectURL(new Blob([value], { type: 'application/json' }));
              const link = document.createElement('a');
              link.href = url;
              link.download = 'document.json';
              link.click();
              // Без отзыва ссылки содержимое остаётся в памяти вкладки
              // до её закрытия.
              URL.revokeObjectURL(url);
            }}
          >
            <Download className="w-4 h-4" />
            {t('download')}
          </ToolButton>
        </>
      )}
    >
      {({ value, onChange, readOnly }) => (
        <JsonWorkspace
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          mode={mode}
          compare={compare}
          other={other}
          onOtherChange={setOther}
          onCloseCompare={() => {
            setCompare(false);
          }}
        />
      )}
    </EditorShell>
  );
}

/** Сообщение о состоянии документа под полем ввода. */
function Status({ text, side }: { text: string; side: 'left' | 'right' }) {
  const t = useTranslations('dh_editor');
  const result = useMemo(() => parseJson(text), [text]);

  return (
    <div className="mt-2 text-sm min-h-[1.5rem]" role="status" aria-live="polite" data-side={side}>
      {!text.trim() ? null : result.ok ? (
        <p className="text-[var(--color-success-500)]">{t('valid')}</p>
      ) : (
        <p className="text-[var(--color-danger-500)] font-mono text-xs leading-relaxed">
          {result.line !== null && <span>{result.line}: </span>}
          {result.message}
        </p>
      )}
    </div>
  );
}

function JsonWorkspace({
  value,
  onChange,
  readOnly,
  mode,
  compare,
  other,
  onOtherChange,
  onCloseCompare,
}: {
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  mode: 'text' | 'tree';
  compare: boolean;
  other: string;
  onOtherChange: (value: string) => void;
  onCloseCompare: () => void;
}) {
  const t = useTranslations('dh_editor');

  const left = useMemo(() => parseJson(value), [value]);
  const right = useMemo(() => parseJson(other), [other]);

  // Различия считаются только по двум разобранным документам: сравнивать
  // сломанный текст бессмысленно, о поломке уже сказано под полем.
  const differences = useMemo(() => {
    if (!compare || !left.ok || !right.ok) return null;
    if (!value.trim() || !other.trim()) return null;

    return diffJson(left.value, right.value);
  }, [compare, left, right, value, other]);

  return (
    <div>
      <div className={compare ? 'grid gap-4 lg:grid-cols-2' : ''}>
        <div className="min-w-0">
          {compare && (
            <p className="text-xs font-medium text-[var(--color-text-muted)] mb-2">
              {t('side_left')}
            </p>
          )}
          <div className="rounded-xl border border-[var(--color-border)] overflow-hidden">
            <JsonEditorPane
              value={value}
              onChange={onChange}
              readOnly={readOnly}
              mode={mode}
              label={t('placeholder_json')}
            />
          </div>
          <Status text={value} side="left" />
        </div>

        {compare && (
          <div className="min-w-0">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[var(--color-text-muted)]">
                {t('side_right')}
              </p>
              <button
                onClick={onCloseCompare}
                aria-label={t('compare_close')}
                className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                {t('compare_close')}
              </button>
            </div>
            <div className="rounded-xl border border-[var(--color-border)] overflow-hidden">
              <JsonEditorPane
                value={other}
                onChange={onOtherChange}
                mode={mode}
                label={t('side_right')}
              />
            </div>
            <Status text={other} side="right" />
          </div>
        )}
      </div>

      {compare && <DiffList entries={differences} />}
    </div>
  );
}

/** Список различий: путь, было, стало. */
function DiffList({ entries }: { entries: ReturnType<typeof diffJson> | null }) {
  const t = useTranslations('dh_editor');

  if (entries === null) {
    return (
      <p className="mt-6 text-sm text-[var(--color-text-muted)]" data-testid="diff-hint">
        {t('diff_hint')}
      </p>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="mt-6 text-sm text-[var(--color-success-500)]" data-testid="diff-equal">
        {t('diff_equal')}
      </p>
    );
  }

  return (
    <div className="mt-6" data-testid="diff-list">
      <h2 className="text-sm font-semibold mb-3">{t('diff_title', { count: entries.length })}</h2>
      <ul className="rounded-xl border border-[var(--color-border)] divide-y divide-[var(--color-border)] overflow-hidden">
        {entries.map((entry) => (
          <li
            key={`${entry.kind}:${entry.path}`}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
          >
            <code className="font-mono text-xs text-[var(--color-text)]">
              {entry.path || t('diff_root')}
            </code>
            <span className="text-xs text-[var(--color-text-muted)]">
              {t(`diff_${entry.kind}`)}
            </span>
            <span className="font-mono text-xs text-[var(--color-text-muted)] ml-auto">
              {shortValue(entry.left)} → {shortValue(entry.right)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
